import {
  DataType,
  type SettingTypeMetadata,
} from '@wix/editor-platform-ooi-metadata-resolver';
import type { SettingTypeValueMap } from '@wix/editor-platform-ooi-metadata-resolver/dist/types/types/metadata/settingsDefinition';
import {
  createSettingsParams,
  createSettingsParam,
  type ISettingsParam,
  SettingsParamType,
} from '@wix/tpa-settings';
import type { ILinkType } from '@wix/yoshi-flow-editor';
import { SelectedPaymentOption } from '../../types/ambassador/bookings/ambassador-bookings-v2-booking';
import {
  AlignmentOptions,
  BorderStyle,
  PaymentMethod,
  PlatformIdentifiers,
} from '../../types/types';
import { TagName } from '../../utils/tagName.const';

export type ISettingsParams = {
  fieldsBorderStyle: BorderStyle;
  pageTitle: SettingsParamType.Text;
  pageTitleTagName: TagName;
  titlesAlignment: AlignmentOptions;
  stretchButtonToFullWidth: SettingsParamType.Boolean;
  selectPaymentTitle: SettingsParamType.Text;
  pricingPlanText: SettingsParamType.Text;
  singleSessionText: SettingsParamType.Text;
  selectPaymentTypeLabel: SettingsParamType.Text;
  payOnlineText: SettingsParamType.Text;
  payOfflineText: SettingsParamType.Text;
  addToCartText: SettingsParamType.Text;
  bookNowButtonText: SettingsParamType.Text;
  checkoutText: SettingsParamType.Text;
  offlinePaymentButtonText: SettingsParamType.Text;
  onlinePaymentButtonText: SettingsParamType.Text;
  requestBookingButtonText: SettingsParamType.Text;
  chooseAPlanText: SettingsParamType.Text;
  summarySectionTitle: SettingsParamType.Text;
  summarySectionTagName: TagName;
  videoConferenceTitle: SettingsParamType.Text;
  summaryPaymentSectionTitle: SettingsParamType.Text;
  videoConferenceBadgeVisibility: SettingsParamType.Boolean;
  defaultPaymentMethod: PaymentMethod;
  defaultPaymentTime: SelectedPaymentOption;
  addMoreSessionsButtonVisible: SettingsParamType.Boolean;
  viewCartButtonText: SettingsParamType.Text;
  bookAnotherSessionButtonText: SettingsParamType.Text;
  cartPopupWhereToLinkContinueBrowsing: null | ILinkType;
  viewPolicyLinkText: SettingsParamType.Text;
  numberOfParticipantsDropdownLabel: SettingsParamType.Text;
  formPageSlot: SettingsParamType.Object;
};

const getMetadata = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
): SettingTypeMetadata => ({
  type: type as DataType.UNKNOWN_DataType, // type assertion is needed to avoid strange TypeScript error
  defaultValue: {
    type: 'static',
    value: defaultValue,
  },
});

const settingsParam = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(type, defaultValue),
});

const enumSettingsParam = <T extends string>(defaultValue: T) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(DataType.textEnum, defaultValue),
});

const typedSettingsParam = <T extends DataType, Type extends SettingsParamType>(
  dataType: T,
  type: Type | undefined,
  defaultValue: SettingTypeValueMap[T]['value'],
) => ({
  type,
  ...settingsParam(dataType, defaultValue),
});

const textSettingsParam = (
  defaultValueKey: string,
): Omit<ISettingsParam<SettingsParamType.Text>, 'key'> => ({
  type: SettingsParamType.Text,
  getDefaultValue: ({ t }) => t(defaultValueKey),
  metadata: ({ t }) => ({
    type: DataType.text,
    defaultValue: {
      type: 'static',
      value: t(defaultValueKey),
    },
  }),
});

const settingsParamWithRTLDefault = <T extends string>(
  rtlDefaultValue: T,
  ltrDefaultValue: T,
): Omit<ISettingsParam, 'key'> => ({
  getDefaultValue: ({ isRTL }) => (isRTL ? rtlDefaultValue : ltrDefaultValue),
  metadata: getMetadata(DataType.text, rtlDefaultValue), // TODO: Fix metadata
});

const booleanSettingsParam = (defaultValue: boolean) =>
  typedSettingsParam(
    DataType.booleanValue,
    SettingsParamType.Boolean,
    defaultValue,
  );

const formPageSlot = createSettingsParam('formPageSlot', {
  type: SettingsParamType.Object,
  getDefaultValue: () => null as any,
  metadata: {
    type: DataType.container,
    dataItemManifestData: {
      container: {
        containerType: 'slot',
        slot: {
          slotId: PlatformIdentifiers.BOOKINGS_FORM_CART_MODAL_SLOT_ID,
        },
      },
    },

    defaultValue: {
      type: 'static',
      value: {},
    },
  },
});

export default createSettingsParams<ISettingsParams>({
  cartPopupWhereToLinkContinueBrowsing: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    null,
  ),
  titlesAlignment: settingsParamWithRTLDefault(
    AlignmentOptions.RIGHT,
    AlignmentOptions.LEFT,
  ),
  fieldsBorderStyle: enumSettingsParam(BorderStyle.BOX),
  pageTitle: textSettingsParam('app.bookings-form.header.placeholder'),
  pageTitleTagName: enumSettingsParam(TagName.MainHeading),
  stretchButtonToFullWidth: booleanSettingsParam(true),
  selectPaymentTitle: textSettingsParam(
    'app.booking-details.form.payment.title',
  ),
  pricingPlanText: textSettingsParam(
    'app.booking-details.payment-options.buy-a-pricing-plan.label',
  ),
  singleSessionText: textSettingsParam(
    'app.booking-details.payment-options.pay-for-single-session.label',
  ),
  selectPaymentTypeLabel: textSettingsParam(
    'app.booking-details.payment-options.when-do-you-want-to-pay.label',
  ),
  payOnlineText: textSettingsParam(
    'app.booking-details.payment-options.pay-online.label',
  ),
  payOfflineText: textSettingsParam(
    'app.booking-details.payment-options.pay-offline.label',
  ),
  addToCartText: textSettingsParam(
    'app.settings.text.form.button.add-to-cart-placeholder',
  ),
  bookNowButtonText: textSettingsParam(
    'app.settings.text.from.button.book-now.placeholder',
  ),
  checkoutText: textSettingsParam(
    'app.booking-details.summary.cta.checkout.label',
  ),
  offlinePaymentButtonText: textSettingsParam(
    'app.booking-details.summary.cta.book.label',
  ),
  onlinePaymentButtonText: textSettingsParam(
    'app.booking-details.summary.cta.pay-now.label',
  ),
  requestBookingButtonText: textSettingsParam(
    'app.booking-details.summary.cta.request-to-book.label',
  ),
  chooseAPlanText: textSettingsParam(
    'app.booking-details.pricing-plans.choose-a-plan-label',
  ),
  summarySectionTitle: textSettingsParam(
    'app.booking-details.booking-summary.text',
  ),
  summarySectionTagName: enumSettingsParam(TagName.SecondaryHeading),
  summaryPaymentSectionTitle: textSettingsParam(
    'app.booking-details.payment-summary.title',
  ),
  videoConferenceTitle: textSettingsParam(
    'app.booking-details.video-conference.text',
  ),
  videoConferenceBadgeVisibility: booleanSettingsParam(true),
  defaultPaymentMethod: enumSettingsParam(PaymentMethod.MEMBERSHIP),
  defaultPaymentTime: enumSettingsParam(SelectedPaymentOption.ONLINE),
  addMoreSessionsButtonVisible: booleanSettingsParam(true),
  viewCartButtonText: textSettingsParam(
    'app.booking-details.cart-modal.cta.checkout.label',
  ),
  bookAnotherSessionButtonText: textSettingsParam(
    'app.booking-details.cart-modal.link.continue-browsing.label',
  ),
  viewPolicyLinkText: textSettingsParam(
    'app.bookings-form.booking-details.booking-policy-button',
  ),
  numberOfParticipantsDropdownLabel: textSettingsParam(
    'app.booking-details.booking-form.number-of-participants.label',
  ),
  formPageSlot,
});
